/**
 * VFXBudgetManager — Tracks active VFX object counts and provides
 * adaptive quality scaling based on FPS to maintain 60fps target.
 *
 * Integrated into VFXEffectPlayer: before spawning each primitive,
 * check canSpawnShape(). When over budget, non-essential primitives
 * (glow/trail layers with depth < 90) are skipped.
 */

export class VFXBudgetManager {
  private static readonly MAX_ACTIVE_SHAPES = 150;
  private static readonly MAX_ACTIVE_GRAPHICS = 30;
  // Raised 50→100 (VFX-evolution M1.S4): the emitter now pools particle
  // display objects (no per-particle GC churn), so denser additive-glow
  // bursts are affordable. getQualityScale still throttles scatter/emission
  // when FPS drops.
  private static readonly MAX_PARTICLES_PER_FRAME = 100;

  private activeShapeCount = 0;
  private activeGraphicsCount = 0;
  private particlesThisFrame = 0;

  /** Check if we can spawn another pooled shape */
  canSpawnShape(): boolean {
    return this.activeShapeCount < VFXBudgetManager.MAX_ACTIVE_SHAPES;
  }

  /** Check if we can spawn another un-pooled Graphics object */
  canSpawnGraphics(): boolean {
    return this.activeGraphicsCount < VFXBudgetManager.MAX_ACTIVE_GRAPHICS;
  }

  /** Check if we can spawn particles this frame */
  canSpawnParticles(count: number): boolean {
    return (this.particlesThisFrame + count) <= VFXBudgetManager.MAX_PARTICLES_PER_FRAME;
  }

  onShapeCreated(isGraphics: boolean): void {
    this.activeShapeCount++;
    if (isGraphics) this.activeGraphicsCount++;
  }

  onShapeDestroyed(isGraphics: boolean): void {
    this.activeShapeCount = Math.max(0, this.activeShapeCount - 1);
    if (isGraphics) this.activeGraphicsCount = Math.max(0, this.activeGraphicsCount - 1);
  }

  onParticlesSpawned(count: number): void {
    this.particlesThisFrame += count;
  }

  /** Call at the start of each frame to reset per-frame counters */
  resetFrame(): void {
    this.particlesThisFrame = 0;
  }

  /**
   * Returns a quality scale (0.0 to 1.0) based on current FPS.
   * Use this to reduce scatter counts and skip glow layers when FPS drops.
   */
  getQualityScale(currentFPS: number): number {
    if (currentFPS >= 55) return 1.0;
    if (currentFPS >= 45) return 0.7;
    if (currentFPS >= 30) return 0.4;
    return 0.2;
  }

  /** Get current stats for diagnostics overlay */
  getStats() {
    return {
      activeShapes: this.activeShapeCount,
      activeGraphics: this.activeGraphicsCount,
      maxShapes: VFXBudgetManager.MAX_ACTIVE_SHAPES,
      maxGraphics: VFXBudgetManager.MAX_ACTIVE_GRAPHICS,
      utilizationPercent: Math.round((this.activeShapeCount / VFXBudgetManager.MAX_ACTIVE_SHAPES) * 100),
    };
  }
}

/** Singleton instance */
export const vfxBudgetManager = new VFXBudgetManager();
